import { Warning } from "phosphor-react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDocumentUrl, getStatements } from "../../../actions/reports";
import {
  AccessType,
  Resource,
} from "../../../authorization/authorization.enum";
import Authorized from "../../../authorization/authorized";
import FullScreenLoader from "../../../components/fullscreen-loader";
import { Button } from "../../../components/library/button";
import { Table, TableColumn } from "../../../components/library/table";
import { useTableState } from "../../../components/library/table/use-table-state";
import Modal from "../../../components/modal";
import dispatch from "../../../middleware";
import { Statements } from "../../../models";
import { ReduxState } from "../../../reducers";
import { downloadFromUrl, formatDate, isLoadingActive } from "../../../utils";

type DocumentUrlObject = {
  summary: string;
  report: string;
};

export default function Reports() {
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.reports.loadingQueue
  );
  const reports = useSelector((state: ReduxState) => state.reports.statements);

  const [isDocumentDownloadingInProgress, setIsDocumentDownloadingInProgress] =
    useState(false);

  const [errorModal, setErrorModal] = useState<{
    active: boolean;
    message?: string;
  }>({ active: false, message: "" });

  const isLoading =
    isLoadingActive(loadingQueue) || isDocumentDownloadingInProgress;

  useEffect(() => {
    dispatch(storeDispatch, getStatements()).catch((error) => {
      setErrorModal({
        active: true,
        message: error?.message ?? "Something went wrong",
      });
    });
  }, []);

  async function handleDownloadClick(statementId: string) {
    if (!statementId) return;
    try {
      setIsDocumentDownloadingInProgress(true);
      const response: DocumentUrlObject = await dispatch(
        storeDispatch,
        getDocumentUrl(statementId)
      );
      if (!response) return;

      await downloadFromUrl(response.summary, `${statementId}-summary.pdf`);
      await downloadFromUrl(response.report, `${statementId}-report.xlsx`);
    } catch (error) {
      setErrorModal({
        active: true,
        message: error?.message ?? "Something went wrong",
      });
    } finally {
      setIsDocumentDownloadingInProgress(false);
      setErrorModal({ active: false, message: "" });
    }
  }

  const DATA_TABLE_COLUMNS: TableColumn<Statements>[] = useMemo(
    () => [
      { label: "Statement ID", key: "_id", width: "130px" },
      { label: "Status", key: "status", width: "100px", align: "center" },
      {
        label: "Month & Year",
        key: "period",
        width: "110px",
        align: "center",
        render: (_, { period }) =>
          period ? formatDate(new Date(period)) : "-",
      },
      {
        label: "Created At",
        key: "createdAt",
        width: "110px",
        align: "center",
        render: (_, { createdAt }) =>
          createdAt ? formatDate(new Date(createdAt)) : "-",
      },
      {
        label: "Actor Email",
        key: "actorEmail",
      },
      {
        label: "Actions",
        key: "_id",
        render: (_, { _id }) => (
          <div style={{ gap: "10px" }} className="d-flex align-items-center">
            <Button
              size="sm"
              variant="primary"
              color="brand"
              onClick={() => handleDownloadClick(_id)}
              children="Download PDF/Excel"
            />
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps } = useTableState({
    initialState: {
      tableColumnVisibility: {
        _id: {
          visible: true,
          disabled: true,
        },
        status: {
          visible: true,
          disabled: true,
        },
        period: {
          visible: true,
          disabled: true,
        },
        createdAt: {
          visible: true,
          disabled: true,
        },
        actorEmail: {
          visible: true,
          disabled: true,
        },
      },
    },
    data: reports,
    columns: DATA_TABLE_COLUMNS,
    config: {
      uniqueKey: "_id",
    },
  });

  return (
    <div>
      <Authorized
        resourceName={Resource.EMPLOYER_PORTAL_REPORTS}
        requiredAccessType={AccessType.READ}
      >
        <>
          <FullScreenLoader active={isLoading} />
          <div className="table-wrapper">
            <div className="row mb-4">
              <div className="col-3 col-md-5">
                <h1 className="primary-color fw-700 fs-24">Reports</h1>
              </div>
            </div>

            <Table {...getTableProps()} data={reports} />
          </div>
        </>
      </Authorized>
      <Modal
        active={errorModal.active}
        onClose={() => setErrorModal({ active: false })}
      >
        <div className="p-3">
          <div className="d-flex align-items-center justify-content-center">
            <Warning size={48} color="#f20202" />
          </div>
          <div>
            <h3 className="fs-20 fw-500 primary-color my-4 text-center">
              {errorModal.message}
            </h3>
            <p className="fs-14 fw-400 primary-color my-2 text-center">
              Please try again later
            </p>
          </div>
          <div className="d-flex align-items-center justify-content-center">
            <Button
              size="lg"
              variant="primary"
              color="brand"
              onClick={() => setErrorModal({ active: false })}
              children="Close"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
